import React, { useRef } from 'react';
import { Upload, Trash2, Lock, Unlock, RefreshCw } from 'lucide-react';

/**
 * PoseMediaControls - Admin buttons floating over a pose header
 * to upload / replace / lock / delete its custom video.
 *
 * Props:
 *   poseId, hasMedia (boolean), locked (boolean)
 *   onUpload(file), onDelete(), onToggleLock()
 */
export default function PoseMediaControls({ poseId, hasMedia, locked, onUpload, onDelete, onToggleLock }) {
  const fileRef = useRef(null);

  return (
    <div
      className="absolute top-4 right-4 z-30 flex flex-col gap-2"
      data-testid={`pose-media-controls-${poseId}`}
    >
      <input
        ref={fileRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = '';
        }}
        data-testid={`pose-media-input-${poseId}`}
      />

      <button
        onClick={() => fileRef.current?.click()}
        disabled={hasMedia && locked}
        className="bg-amber-500/90 hover:bg-amber-400 disabled:bg-slate-700 disabled:cursor-not-allowed text-slate-900 rounded-full px-3 py-1.5 flex items-center gap-1.5 transition shadow-lg text-xs font-medium"
        data-testid={`pose-media-upload-${poseId}`}
        title={hasMedia ? 'Remplacer la vidéo' : 'Charger une vidéo'}
      >
        {hasMedia ? <RefreshCw className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />}
        {hasMedia ? 'Remplacer' : 'Charger vidéo'}
      </button>

      {hasMedia && (
        <>
          <button
            onClick={onToggleLock}
            className={`rounded-full px-3 py-1.5 flex items-center gap-1.5 transition shadow-lg text-xs font-medium ${
              locked
                ? 'bg-amber-500/90 text-slate-900 hover:bg-amber-400'
                : 'bg-slate-700/90 text-white hover:bg-slate-600'
            }`}
            data-testid={`pose-media-lock-${poseId}`}
            title={locked ? 'Déverrouiller' : 'Verrouiller'}
          >
            {locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
            {locked ? 'Verrouillée' : 'Verrouiller'}
          </button>

          {!locked && (
            <button
              onClick={onDelete}
              className="bg-red-600/90 hover:bg-red-500 text-white rounded-full px-3 py-1.5 flex items-center gap-1.5 transition shadow-lg text-xs font-medium"
              data-testid={`pose-media-delete-${poseId}`}
              title="Supprimer la vidéo"
            >
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </button>
          )}
        </>
      )}
    </div>
  );
}
